import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import adminClient from '@/api/adminClient';
import type { TeamMember, UserInvitation, UserRole, UserStatus } from '@/types';

export default function TeamManagementPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  // Form states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('member');
  const [isInviting, setIsInviting] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Edit credentials form
  const [editForm, setEditForm] = useState({ email: '', password: '', displayName: '' });
  const [isSavingCredentials, setIsSavingCredentials] = useState(false);

  const isOwner = user?.role === 'owner';
  const canInvite = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [usersData, invitationsData] = await Promise.all([
        adminClient.listUsers(),
        adminClient.listInvitations(),
      ]);
      setMembers(usersData.users);
      setInvitations(invitationsData);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    try {
      setIsInviting(true);
      setError('');
      await adminClient.inviteUser(inviteEmail.trim(), inviteRole);
      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('member');
      setShowInviteModal(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      setActionLoading(invitationId);
      setError('');
      await adminClient.cancelInvitation(invitationId);
      setSuccess('Invitation cancelled');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to cancel invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: UserRole) => {
    if (memberId === user?.id) return;

    try {
      setActionLoading(memberId);
      setError('');
      await adminClient.updateUserRole(memberId, newRole);
      setSuccess('Role updated successfully');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStatusToggle = async (member: TeamMember) => {
    if (member.id === user?.id || member.role === 'owner') return;

    const newStatus: UserStatus = member.status === 'active' ? 'suspended' : 'active';

    try {
      setActionLoading(member.id);
      setError('');
      await adminClient.updateUserStatus(member.id, newStatus);
      setSuccess(`User ${newStatus === 'suspended' ? 'suspended' : 'activated'}`);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (memberId: string) => {
    try {
      setActionLoading(memberId);
      setError('');
      await adminClient.deleteUser(memberId);
      setSuccess('User deleted successfully');
      setShowDeleteModal(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = (member: TeamMember) => {
    setEditingMember(member);
    setEditForm({
      email: member.email,
      password: '',
      displayName: member.displayName || '',
    });
  };

  const closeEditModal = () => {
    setEditingMember(null);
    setEditForm({ email: '', password: '', displayName: '' });
  };

  const handleSaveCredentials = async () => {
    if (!editingMember) return;

    const updates: { email?: string; password?: string; displayName?: string } = {};

    if (editForm.email && editForm.email !== editingMember.email) {
      updates.email = editForm.email;
    }
    if (editForm.password) {
      if (editForm.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
      updates.password = editForm.password;
    }
    if (editForm.displayName !== (editingMember.displayName || '')) {
      updates.displayName = editForm.displayName;
    }

    if (Object.keys(updates).length === 0) {
      setError('No changes to save');
      return;
    }

    try {
      setIsSavingCredentials(true);
      setError('');
      await adminClient.updateUserCredentials(editingMember.id, updates);
      setSuccess('User credentials updated successfully');
      closeEditModal();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update credentials');
    } finally {
      setIsSavingCredentials(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRoleBadgeStyle = (role: UserRole): React.CSSProperties => {
    const colors: Record<UserRole, { bg: string; text: string }> = {
      owner: { bg: '#e9d5ff', text: '#7c3aed' },
      admin: { bg: '#dbeafe', text: '#2563eb' },
      operator: { bg: '#d1fae5', text: '#059669' },
      member: { bg: '#f3f4f6', text: '#6b7280' },
    };
    return {
      ...styles.badge,
      backgroundColor: colors[role].bg,
      color: colors[role].text,
    };
  };

  const getStatusBadgeStyle = (status: UserStatus): React.CSSProperties => {
    const colors: Record<UserStatus, { bg: string; text: string }> = {
      active: { bg: '#d1fae5', text: '#059669' },
      invited: { bg: '#fef3c7', text: '#d97706' },
      suspended: { bg: '#fee2e2', text: '#dc2626' },
    };
    return {
      ...styles.badge,
      backgroundColor: colors[status].bg,
      color: colors[status].text,
    };
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading team data...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Team Management</h1>
          <p style={styles.subtitle}>Manage your organization's team members</p>
        </div>
        {canInvite && (
          <button
            onClick={() => setShowInviteModal(true)}
            style={styles.primaryButton}
          >
            + Invite Member
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div style={styles.errorAlert}>
          {error}
          <button onClick={() => setError('')} style={styles.alertClose}>x</button>
        </div>
      )}
      {success && (
        <div style={styles.successAlert}>
          {success}
          <button onClick={() => setSuccess('')} style={styles.alertClose}>x</button>
        </div>
      )}

      {/* Active Members Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          Active Members ({members.filter(m => m.status === 'active').length})
        </h2>
        <div style={styles.cardList}>
          {members.filter(m => m.status === 'active').map((member) => (
            <div key={member.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.avatar}>
                  {(member.displayName || member.email).charAt(0).toUpperCase()}
                </div>
                <div style={styles.memberInfo}>
                  <div style={styles.memberName}>
                    {member.displayName || member.email.split('@')[0]}
                    {member.id === user?.id && <span style={styles.youBadge}>(You)</span>}
                  </div>
                  <div style={styles.memberEmail}>{member.email}</div>
                </div>
              </div>

              <div style={styles.cardMeta}>
                <div style={styles.metaRow}>
                  <span style={styles.metaLabel}>Role:</span>
                  {isOwner && member.role !== 'owner' && member.id !== user?.id ? (
                    <select
                      value={member.role}
                      onChange={(e) => handleRoleChange(member.id, e.target.value as UserRole)}
                      disabled={actionLoading === member.id}
                      style={styles.roleSelect}
                    >
                      <option value="admin">Admin</option>
                      <option value="operator">Operator</option>
                      <option value="member">Member</option>
                    </select>
                  ) : (
                    <span style={getRoleBadgeStyle(member.role)}>
                      {member.role.toUpperCase()}
                    </span>
                  )}
                </div>
                <div style={styles.metaRow}>
                  <span style={styles.metaLabel}>Status:</span>
                  <span style={getStatusBadgeStyle(member.status)}>
                    {member.status.toUpperCase()}
                  </span>
                </div>
                <div style={styles.metaRow}>
                  <span style={styles.metaLabel}>Last login:</span>
                  <span style={styles.metaValue}>{formatDate(member.lastLoginAt)}</span>
                </div>
              </div>

              {isOwner && member.id !== user?.id && (
                <div style={styles.cardActions}>
                  <button
                    onClick={() => openEditModal(member)}
                    disabled={actionLoading === member.id}
                    style={styles.editButton}
                  >
                    Edit Credentials
                  </button>
                  {member.role !== 'owner' && (
                    <>
                      <button
                        onClick={() => handleStatusToggle(member)}
                        disabled={actionLoading === member.id}
                        style={member.status === 'active' ? styles.warningButton : styles.successButton}
                      >
                        {actionLoading === member.id ? '...' : member.status === 'active' ? 'Suspend' : 'Activate'}
                      </button>
                      <button
                        onClick={() => setShowDeleteModal(member.id)}
                        disabled={actionLoading === member.id}
                        style={styles.dangerButton}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Suspended Members Section */}
      {members.filter(m => m.status === 'suspended').length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            Suspended Members ({members.filter(m => m.status === 'suspended').length})
          </h2>
          <div style={styles.cardList}>
            {members.filter(m => m.status === 'suspended').map((member) => (
              <div key={member.id} style={{ ...styles.card, opacity: 0.7 }}>
                <div style={styles.cardHeader}>
                  <div style={{ ...styles.avatar, backgroundColor: '#9ca3af' }}>
                    {(member.displayName || member.email).charAt(0).toUpperCase()}
                  </div>
                  <div style={styles.memberInfo}>
                    <div style={styles.memberName}>
                      {member.displayName || member.email.split('@')[0]}
                    </div>
                    <div style={styles.memberEmail}>{member.email}</div>
                  </div>
                </div>
                <div style={styles.cardMeta}>
                  <span style={getStatusBadgeStyle('suspended')}>SUSPENDED</span>
                </div>
                {isOwner && (
                  <div style={styles.cardActions}>
                    <button
                      onClick={() => handleStatusToggle(member)}
                      disabled={actionLoading === member.id}
                      style={styles.successButton}
                    >
                      {actionLoading === member.id ? '...' : 'Activate'}
                    </button>
                    <button
                      onClick={() => setShowDeleteModal(member.id)}
                      disabled={actionLoading === member.id}
                      style={styles.dangerButton}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Invitations Section */}
      {invitations.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            Pending Invitations ({invitations.length})
          </h2>
          <div style={styles.cardList}>
            {invitations.map((invitation) => (
              <div key={invitation.id} style={styles.invitationCard}>
                <div style={styles.invitationInfo}>
                  <div style={styles.invitationEmail}>{invitation.email}</div>
                  <div style={styles.invitationMeta}>
                    <span style={getRoleBadgeStyle(invitation.role)}>
                      {invitation.role.toUpperCase()}
                    </span>
                    <span style={styles.invitationExpires}>
                      Expires: {formatDate(invitation.expiresAt)}
                    </span>
                  </div>
                </div>
                {canInvite && (
                  <button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    disabled={actionLoading === invitation.id}
                    style={styles.cancelButton}
                  >
                    {actionLoading === invitation.id ? '...' : 'Cancel'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div style={styles.modalOverlay} onClick={() => setShowInviteModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Invite Team Member</h2>
              <button
                onClick={() => setShowInviteModal(false)}
                style={styles.modalClose}
              >
                x
              </button>
            </div>
            <form onSubmit={handleInvite}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  disabled={isInviting}
                  style={styles.input}
                  placeholder="user@example.com"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Role *</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as UserRole)}
                  disabled={isInviting}
                  style={styles.input}
                >
                  <option value="member">Member</option>
                  <option value="operator">Operator</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div style={styles.roleDescription}>
                <strong>Role Permissions:</strong>
                <ul style={styles.roleList}>
                  <li><strong>Admin:</strong> Can invite members, manage rooms</li>
                  <li><strong>Operator:</strong> Can manage audio input, view dashboard</li>
                  <li><strong>Member:</strong> Read-only access to dashboard</li>
                </ul>
              </div>
              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  disabled={isInviting}
                  style={styles.secondaryButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isInviting || !inviteEmail.trim()}
                  style={styles.primaryButton}
                >
                  {isInviting ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Delete Member</h2>
            </div>
            <p style={styles.modalText}>
              Are you sure you want to delete this member? This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowDeleteModal(null)}
                style={styles.secondaryButton}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal)}
                disabled={actionLoading === showDeleteModal}
                style={styles.dangerButton}
              >
                {actionLoading === showDeleteModal ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Credentials Modal */}
      {editingMember && (
        <div style={styles.modalOverlay} onClick={closeEditModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Edit User Credentials</h2>
              <button onClick={closeEditModal} style={styles.modalClose}>×</button>
            </div>
            <div style={styles.modalInfo}>
              <strong>User:</strong> {editingMember.displayName || editingMember.email}<br />
              <strong>Role:</strong> {editingMember.role.toUpperCase()}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                style={styles.input}
                placeholder="user@example.com"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Display Name</label>
              <input
                type="text"
                value={editForm.displayName}
                onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                style={styles.input}
                placeholder="User Name"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                New Password <span style={styles.hintText}>(leave empty to keep current)</span>
              </label>
              <input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                style={styles.input}
                placeholder="New password (min 6 characters)"
              />
            </div>
            <div style={styles.modalActions}>
              <button onClick={closeEditModal} style={styles.secondaryButton} disabled={isSavingCredentials}>
                Cancel
              </button>
              <button
                onClick={handleSaveCredentials}
                style={styles.primaryButton}
                disabled={isSavingCredentials}
              >
                {isSavingCredentials ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: '#6b7280',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '16px',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#667eea',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  memberEmail: {
    fontSize: '14px',
    color: '#6b7280',
  },
  youBadge: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: 'normal',
  },
  cardMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  metaLabel: {
    fontSize: '13px',
    color: '#6b7280',
  },
  metaValue: {
    fontSize: '13px',
    color: '#374151',
  },
  badge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  roleSelect: {
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    cursor: 'pointer',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    borderTop: '1px solid #e5e7eb',
    paddingTop: '12px',
    marginTop: '4px',
  },
  invitationCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  invitationInfo: {
    flex: 1,
  },
  invitationEmail: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#1f2937',
    marginBottom: '4px',
  },
  invitationMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  invitationExpires: {
    fontSize: '13px',
    color: '#6b7280',
  },
  primaryButton: {
    padding: '10px 16px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '10px 16px',
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  warningButton: {
    padding: '6px 12px',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    border: '1px solid #fcd34d',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  successButton: {
    padding: '6px 12px',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: '1px solid #6ee7b7',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  dangerButton: {
    padding: '6px 12px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #fca5a5',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  cancelButton: {
    padding: '6px 12px',
    backgroundColor: 'white',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  errorAlert: {
    padding: '12px 16px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '6px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  successAlert: {
    padding: '12px 16px',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    borderRadius: '6px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  alertClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '0 4px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
    margin: 0,
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '0 4px',
  },
  modalText: {
    fontSize: '14px',
    color: '#4b5563',
    marginBottom: '20px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '20px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  roleDescription: {
    fontSize: '13px',
    color: '#6b7280',
    backgroundColor: '#f9fafb',
    padding: '12px',
    borderRadius: '6px',
    marginTop: '8px',
  },
  roleList: {
    margin: '8px 0 0 0',
    paddingLeft: '20px',
  },
  editButton: {
    padding: '6px 12px',
    backgroundColor: '#e0e7ff',
    color: '#4338ca',
    border: '1px solid #a5b4fc',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  modalInfo: {
    backgroundColor: '#f3f4f6',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '16px',
    fontSize: '14px',
    color: '#374151',
  },
  hintText: {
    color: '#6b7280',
    fontSize: '12px',
    fontWeight: 'normal',
  },
};
